import { t } from '../i18n';
import { useEffect, useRef, useState } from 'react';
import type { Ingest, SrtNetwork, StreamKey } from '../api';
import type { SrtLink } from './types';

/*
 * SRT ingest in the cabinet (docs/srt-ingest.md §2, §3). The address the UI
 * shows by itself is the public template of /api/ingest with the key's srt_id
 * filled in: no passphrase. The full address (the stream key as passphrase)
 * comes only from GET /api/stream-keys/{id}/reveal, on Show or Copy.
 */

export type ObsProtocol = 'rtmps' | 'srt';

export interface SrtOffer {
  /** public address for OBS without the passphrase; absent = SRT can't be offered */
  address?: string;
  /** why SRT is not offered (shown next to the disabled option) */
  why?: string;
}

/** Never show a passphrase that a template should not carry in the first place. */
function withoutPassphrase(url: string): string {
  return url.replace(/([?&])passphrase=[^&]*(&|$)/i, (_m, pre: string, post: string) => (post ? pre : '')).replace(/[?&]$/, '');
}

/**
 * SRT for the user's region: regions[].srt of the chosen region when it has an
 * online node (docs/controlapi.yaml IngestRegion.srt), otherwise Ingest.srt
 * (Auto, or a saved region with no node online: the automatic addresses).
 */
export function srtOffer(ingest: Ingest | undefined, key: StreamKey | undefined): SrtOffer {
  if (!ingest) return {};
  const chosen = ingest.region ? ingest.regions.find((r) => r.region === ingest.region) : undefined;
  const template = chosen ? chosen.srt : ingest.srt;
  if (!template) {
    const others = ingest.regions.filter((r) => r.srt && r.region !== chosen?.region).map((r) => r.region);
    const elsewhere = others.length ? t('srt.elsewhere', { list: others.join(', ') }) : '';
    return {
      why: chosen
        ? t(chosen.group ? 'srt.noneInGroup' : 'srt.noneIn', { region: chosen.region }) + elsewhere
        : t('srt.noneHere') + elsewhere,
    };
  }
  if (!key || key.revoked_at || !key.srt_id) return { why: t('srt.needKey') };
  return { address: withoutPassphrase(template.split('{srt_id}').join(key.srt_id)) };
}

/**
 * Chosen protocol. Starts on SRT when the stream already comes in over SRT,
 * until the user picks one themselves.
 */
export function useObsProtocol(current?: string): [ObsProtocol, (p: ObsProtocol) => void] {
  const [proto, setProto] = useState<ObsProtocol>(current === 'srt' ? 'srt' : 'rtmps');
  const touched = useRef(false);
  useEffect(() => {
    if (!touched.current && current === 'srt') setProto('srt');
  }, [current]);
  return [proto, (p) => { touched.current = true; setProto(p); }];
}

export interface SrtCell {
  id: string;
  label: string;
  value: string;
  hint: string;
}

const ms = (v?: number) => (v === undefined ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v)} ms`);
const count = (v?: number) => (v === undefined ? '—' : String(v));

/**
 * The SRT block of an input, as neutral facts. Loss and retransmissions are how
 * SRT works on a lossy path; whether the input has a problem is decided by
 * state_reason / health, never here.
 */
export function srtCells(n: SrtLink): SrtCell[] {
  return [
    { id: 'rtt', label: 'RTT', value: ms(n.rttMs), hint: 'Round trip between your encoder and the server' },
    { id: 'latency', label: 'Latency', value: ms(n.latencyMs), hint: 'SRT latency agreed with your encoder: how long the server waits for lost packets to be resent' },
    { id: 'buffer', label: 'Buffer', value: ms(n.recvBufMs), hint: 'Data received and waiting for its play-out time' },
    { id: 'lost', label: 'Lost', value: count(n.lost), hint: 'Packets found missing in the last 5 s and asked for again' },
    { id: 'resent', label: 'Resent', value: count(n.resent), hint: 'Resent packets received in the last 5 s' },
    { id: 'late', label: 'Late', value: count(n.late), hint: 'Packets that did not arrive in time in the last 5 s' },
    { id: 'rate', label: 'Rate', value: n.kbps === undefined ? '—' : `${(n.kbps / 1000).toFixed(1)} Mbps`, hint: 'Receiving rate over the last 5 s' },
    { id: 'cipher', label: 'Cipher', value: n.cipher ? n.cipher.toUpperCase() : '—', hint: 'Encryption of the SRT connection' },
  ];
}

/** The API's SRT block as the view model. */
export function srtLink(n: SrtNetwork): SrtLink {
  return {
    rttMs: n.rtt_ms, latencyMs: n.latency_ms, recvBufMs: n.recv_buf_ms, lost: n.pkt_loss_win, resent: n.pkt_retrans_win,
    late: n.pkt_drop_win, kbps: n.bw_kbps, cipher: n.cipher,
  };
}

/** Protocol name for display: SRT, RTMPS, RTMP. */
export const protocolLabel = (p?: string) => (p ? p.toUpperCase() : undefined);
